import Backbone from '@rover/globals/backbone';
import _ from '@rover/globals/underscore';
import { formatCurrency } from '@rover/utilities/currency';
import inject from '@rover/utilities/injector';

export default Backbone.Model.extend({
  defaults: {
    roverBucksBalance: null,
    requestableTotalPrice: null,
  },
  initialize(optionset) {
    const options = optionset || {};
    _.bindAll(this, 'updateRequestableTotalPrice', 'updateRoverBucksBalance');
    this.requestableModel = options.requestableModel;
    this.roverBucksModel = options.roverBucksModel;
    this.listenTo(this.requestableModel, 'change:total_price', this.updateRequestableTotalPrice);
    this.listenTo(this.roverBucksModel, 'change', this.updateRoverBucksBalance);
    this.updateRoverBucksBalance();
  },
  updateRequestableTotalPrice() {
    this.set('requestableTotalPrice', this.requestableModel.get('total_price'));
  },
  updateRoverBucksBalance() {
    this.set('roverBucksBalance', this.roverBucksModel.getTotalRoverBucks());
  },
  roverBucksCoverCost(totalPrice) {
    const requestableTotalPrice = this.getRequestableTotalPrice(totalPrice);
    return parseFloat(this.get('roverBucksBalance')) >= requestableTotalPrice;
  },
  getRequestableTotalPrice(totalPrice) {
    if (_.isUndefined(totalPrice)) {
      return parseFloat(this.get('requestableTotalPrice'));
    }
    return totalPrice;
  },
  calculateAmountToPayForPaymentIntent() {
    /*
      This unfortunate method exists because the serializer for recurring billing
      currently expects that the payment info passed into it will match the price of a normal week,
      not a reduced first-week price (despite the fact that the API endpoint will correctly charge
      the user a lower amount if needed). However, we need to pass the correct amount into the PaymentIntent
      (DEV-58149) so we use this method to calculate the correct active week amount for the PaymentIntent
      while continuing to use the potentially incorrect `amountToPay` created in `getTotalPrice`
      for everything else.
     */
    let requestableTotalPrice = parseFloat(this.get('requestableTotalPrice'));
    if (this.requestableModel.isRecurring) {
      requestableTotalPrice = parseFloat(
        this.requestableModel.get('active_conversation').total_price
      );
    }

    const { amountToPay } = this.getAmountToPay(requestableTotalPrice);

    return amountToPay;
  },
  getAmountToPay(totalPrice) {
    const requestableTotalPrice = this.getRequestableTotalPrice(totalPrice);
    let roverBucksBalance = null;
    let amountToPay = requestableTotalPrice;
    let roverBucksToUse = roverBucksBalance;

    if (this.get('roverBucksBalance') !== null) {
      roverBucksBalance = parseFloat(this.get('roverBucksBalance'));
    }
    roverBucksToUse = roverBucksBalance;

    // Only return total price if we know what the roverBucksBalance is, even if 0
    if (this.requestableModel.isPayingState() && roverBucksBalance === null) {
      return false;
    }

    if (this.roverBucksCoverCost(requestableTotalPrice)) {
      amountToPay = 0;
      roverBucksToUse = requestableTotalPrice;
    } else if (roverBucksBalance > 0) {
      amountToPay = requestableTotalPrice - roverBucksBalance;
    } else if (roverBucksBalance < 0) {
      roverBucksToUse = 0;
    }

    amountToPay = Math.round(amountToPay * 100) / 100;

    return { amountToPay, roverBucksToUse };
  },
  getTotalPrice() {
    const requestableTotalPrice = parseFloat(this.get('requestableTotalPrice'));
    const totalPriceCurrencyIso = this.requestableModel.get('total_price_obj').currency_iso;
    const stayPriceCurrencyIso = this.requestableModel.get('stay_price_obj').currency_iso;
    const { amountToPay, roverBucksToUse } = this.getAmountToPay();

    const locale = inject('Rover.context.language', undefined);

    return {
      amountToPay: amountToPay.toFixed(2),
      amountForPaymentIntent: this.calculateAmountToPayForPaymentIntent(),
      totalPriceCurrencyIso,
      amountToPayDisplay: formatCurrency(amountToPay.toFixed(2), totalPriceCurrencyIso, locale),
      roverBucksToUse: roverBucksToUse.toFixed(2),
      roverBucksToUseDisplay: formatCurrency(
        roverBucksToUse.toFixed(2),
        totalPriceCurrencyIso,
        locale
      ),
      totalPrice: requestableTotalPrice.toFixed(2),
      totalPriceDisplay: formatCurrency(
        requestableTotalPrice.toFixed(2),
        totalPriceCurrencyIso,
        locale
      ),
      stayPrice: this.requestableModel.get('stay_price'),
      stayPriceCurrencyIso,
      stayPriceDisplay: formatCurrency(
        this.requestableModel.get('stay_price'),
        stayPriceCurrencyIso,
        locale
      ),
    };
  },
});



// WEBPACK FOOTER //
// ./js/backbone/models/PriceModel.js